import { selectUser } from 'oidc/user.selectors';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { editExperienceSelector } from '../../Profile/CandidateDetails.selector';
import { GenericDialog } from '@AMIEWEB/Alerts/GenericDialog';
import { Typography } from '@mui/material';
import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { EditTimeOffForm, IEditTimeOffForm } from './components/EditTimeOffForm';
import { makeWorkExperienceDialogueStyles } from './EditWorkExperienceDialogue.styles';
import { useReadOnly } from 'oidc/userRoles';
import { candidateDetailActions } from '../../Profile/CandidateDetails.redux';
import { ExperienceEditType } from 'app/models/Candidate/CandidateProfile';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import { Cancel } from '@AMIEWEB/Common/CancelModal/Cancel';
import { yupResolver } from '@hookform/resolvers/yup';
import { timeOffFormValidator } from './FormResolver/EditTimeOffFormResolver';
import { CustomBackdrop } from '@AMIEWEB/Common/Backdrop/CustomBackdrop';
import { postCandidateWorkExperience } from 'app/services/CandidateServices/CandidateServices';
import { missingField } from 'app/constants';
import { globalActions } from 'app/ApplicationRoot/Global.redux';
import { getPayloadForTimeOff } from './Helpers/FormDataHelper';

export const EditTimeOffDialog = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [isLoading, setLoading] = useState(false);
  const editExperience = useSelector(editExperienceSelector);
  const user = useSelector(selectUser);
  const { classes } = makeWorkExperienceDialogueStyles();
  const [cancel, setCancel] = useState<boolean>(false);
  const [saveDisabled, setSaveDisabled] = useState<boolean>(false);
  const { readOnly } = useReadOnly();
  const dialogTitle = t('candidate.dialogActions.TimeOff');

  const { travelerId: candidateId, brandId } = useParams<{ travelerId: string; brandId: string }>();

  const formattedTimeOff: IEditTimeOffForm = {
    start: editExperience?.timeOff?.startedOn,
    end: editExperience?.timeOff?.endedOn,
    isOnGoing: false,
    reason: editExperience?.timeOff?.reasonForLeaving,
  };

  const formMethods = useForm({ defaultValues: formattedTimeOff, resolver: yupResolver(timeOffFormValidator) });
  const {
    reset,
    handleSubmit,
    control,
    formState: { dirtyFields, errors, touchedFields},
  } = formMethods;

  useEffect(() => {
    if (editExperience.timeOff) {
      reset(formattedTimeOff);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editExperience.timeOff]);

  const handleCancel = () => {
    if (!_.isEmpty(errors) || !_.isEmpty(dirtyFields) || !_.isEmpty(touchedFields)) setCancel(true);
    else yesAction();
  };

  const yesAction = () => {
    setCancel(false);
    dispatch(
      candidateDetailActions.setEditExperience({
        workExperience: undefined,
        timeOff: undefined,
        type: ExperienceEditType.None,
        isModalOpen: false,
        rowData: undefined,
      }),
    );
  };
  const noAction = () => {
    setCancel(false);
  };

  const onSubmit = async data => {
    setLoading(true);
    const payload = getPayloadForTimeOff(data, editExperience?.rowData, candidateId, brandId, user);
    const response = await postCandidateWorkExperience(payload);
    if (response?.status == 200) {
      const missingComponent = (
        <Typography component="span" style={{ color: '#a3a2a2' }}>
          {missingField}
        </Typography>
      );
      dispatch(candidateDetailActions.updateWorkExperienceGrid({ response, row: payload, missingComponent }));
      dispatch(
        globalActions.setSnackBar({
          message: 'Time Off details have been updated successfully',
          severity: 'success',
        }),
      );
      yesAction();
    } else {
      dispatch(
        globalActions.setSnackBar({
          message: t('common.failedLoad'),
          severity: 'error',
        }),
      );
    }
    setLoading(false);
  };

  const onError = error => {
    console.log('form error', error);
  };

  return (
    <FormProvider {...formMethods}>
      <form onSubmit={handleSubmit(onSubmit, onError)}>
        <GenericDialog
          variant={'blue'}
          classes={{ paper: classes.dialogContainer }}
          open={editExperience?.isModalOpen ?? false}
          disablePortal
          disableEscapeKeyDown
          dialogTitleProps={{
            text: dialogTitle,
            closeButton: true,
          }}
          dialogActions={[
            {
              onClick: handleCancel,
              text: 'CANCEL',
              variant: 'contained',
              color: 'tertiary',
            },
            {
              text: `${t('candidate.dialogActions.Save')}`,
              variant: 'contained',
              type: 'submit',
              disabled: saveDisabled || readOnly,
              tooltipProps: readOnly
                ? {
                    tooltipContent: t('global.readOnlyTooltip'),
                  }
                : undefined,
            },
          ]}
          dialogContentProps={{
            classes: { root: classes.dialogContent },
          }}
          onClose={handleCancel}
        >
          <>
            <EditTimeOffForm setSaveDisabled={setSaveDisabled} workHistoryId={editExperience?.rowData?.workHistoryId} />
            <CustomBackdrop open={isLoading} />
          </>
        </GenericDialog>
        {cancel && <Cancel openDialog={cancel} handleConfirmAction={yesAction} handleCancelAction={noAction} />}
      </form>
    </FormProvider>
  );
};
